/**
 * 発行日・有効期限の入力。カレンダーから選ぶか、yyyy/mm/dd で入力して Enter。
 * 入力欄から離れたときも、日付として正しければ確定する。
 */
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { isImeEnter, parseDateInput, todayIso } from './model';

interface Props {
  label: string;
  /** YYYY-MM-DD ('' = 未入力) */
  value: string;
  /** 未入力のときにカレンダーで最初に見せる日 */
  fallbackIso: string;
  onCommit: (iso: string) => void;
  onClose: () => void;
}

const WEEKDAYS = ['日', '月', '火', '水', '木', '金', '土'];

export function DatePickerPopover({ label, value, fallbackIso, onCommit, onClose }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const doneRef = useRef(false);
  const [text, setText] = useState(value ? value.replace(/-/g, '/') : '');
  const [error, setError] = useState(false);
  const start = value || fallbackIso || todayIso();
  const [view, setView] = useState(() => {
    const [y, m] = start.split('-').map(Number);
    return { y, m };
  });

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  const finish = (iso: string | null) => {
    if (doneRef.current) return;
    doneRef.current = true;
    if (iso && iso !== value) onCommit(iso);
    onClose();
  };

  const typed = parseDateInput(text);
  const selected = typed ?? value;
  const today = todayIso();
  const firstWeekday = new Date(view.y, view.m - 1, 1).getDay();
  const days = new Date(view.y, view.m, 0).getDate();

  const shiftMonth = (n: number) =>
    setView(({ y, m }) => {
      const d = new Date(y, m - 1 + n, 1);
      return { y: d.getFullYear(), m: d.getMonth() + 1 };
    });

  return (
    <div
      ref={boxRef}
      className="absolute right-0 top-full mt-2 z-30 w-[288px] max-w-[calc(100vw-32px)] rounded-lg border border-[var(--border)] bg-[var(--bg-surface)] text-[var(--text-primary)] shadow-lg p-3 space-y-2.5 text-left"
      // カレンダーのボタンを押しても日付欄から入力の焦点を外さない
      onMouseDown={(e) => {
        if (!(e.target as HTMLElement).closest('input')) e.preventDefault();
      }}
    >
      <div className="text-xs font-semibold text-[var(--text-secondary)]">{label}</div>
      <input
        ref={inputRef}
        value={text}
        placeholder="yyyy/mm/dd"
        aria-label={`${label}を yyyy/mm/dd で入力`}
        autoComplete="off"
        className="w-full h-9 px-2.5 rounded-md border border-[var(--border)] bg-[var(--bg-input)] text-sm tabular-nums focus:outline-none focus:border-[var(--accent)]"
        onChange={(e) => {
          setText(e.target.value);
          setError(false);
          const iso = parseDateInput(e.target.value);
          if (iso) {
            const [y, m] = iso.split('-').map(Number);
            setView({ y, m });
          }
        }}
        onKeyDown={(e) => {
          if (e.key !== 'Enter' || isImeEnter(e)) return;
          e.preventDefault();
          if (text.trim() === '') return finish(null);
          const iso = parseDateInput(text);
          if (!iso) return setError(true);
          finish(iso);
        }}
        onBlur={(e) => {
          if (e.relatedTarget && boxRef.current?.contains(e.relatedTarget as Node)) return;
          finish(parseDateInput(text));
        }}
      />
      {error && (
        <p className="text-[11px] text-[var(--accent-red)]">
          yyyy/mm/dd の形式で入力してください（例: 2026/08/26）
        </p>
      )}
      <div className="flex items-center justify-between text-sm font-semibold">
        <button
          type="button"
          aria-label="前の月"
          onClick={() => shiftMonth(-1)}
          className="p-1 rounded hover:bg-[var(--bg-hover)]"
        >
          <ChevronLeft className="size-4" />
        </button>
        <span className="tabular-nums">
          {view.y}年{view.m}月
        </span>
        <button
          type="button"
          aria-label="次の月"
          onClick={() => shiftMonth(1)}
          className="p-1 rounded hover:bg-[var(--bg-hover)]"
        >
          <ChevronRight className="size-4" />
        </button>
      </div>
      <div className="grid grid-cols-7 gap-0.5 text-center">
        {WEEKDAYS.map((w) => (
          <span key={w} className="text-[11px] text-[var(--text-muted)] py-0.5">
            {w}
          </span>
        ))}
        {Array.from({ length: firstWeekday }, (_, i) => (
          <span key={`blank-${i}`} />
        ))}
        {Array.from({ length: days }, (_, i) => {
          const d = i + 1;
          const iso = `${view.y}-${String(view.m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
          const isSel = iso === selected;
          return (
            <button
              key={iso}
              type="button"
              aria-label={`${view.y}年${view.m}月${d}日`}
              aria-pressed={isSel}
              onClick={() => finish(iso)}
              className={`py-1.5 rounded-md text-[13px] tabular-nums ${
                isSel ? 'bg-[var(--accent)] text-white' : 'hover:bg-[var(--bg-hover)]'
              } ${iso === today && !isSel ? 'ring-1 ring-inset ring-[var(--accent)]' : ''}`}
            >
              {d}
            </button>
          );
        })}
      </div>
      <p className="text-[11px] text-[var(--text-muted)]">
        日付をクリック、または入力して Enter で確定します。
      </p>
    </div>
  );
}
